import { cn } from '@/lib/utils';
import { Button } from '@startupquickstart/frontend';
import { Volume2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { parseGuessSong } from './types';
import { DemoNotConfigured } from './DemoChrome';
import type { DemoChallenge } from './types';

type Feedback = 'idle' | 'wrong' | 'won';

const normalize = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');

/** Web replica of the mobile guess-the-song minigame (preview only). */
export function GuessSongDemo({ challenge }: { challenge: DemoChallenge }) {
  const { audioUrl, songTitle, artist, playDuration } = parseGuessSong(challenge.minigameConfig);
  const [guess, setGuess] = useState('');
  const [feedback, setFeedback] = useState<Feedback>('idle');

  useEffect(() => {
    setGuess('');
    setFeedback('idle');
  }, [challenge.id]);

  if (!audioUrl) {
    return (
      <DemoNotConfigured>
        This song challenge isn&apos;t fully configured yet — no audio clip set.
      </DemoNotConfigured>
    );
  }

  const submit = () => {
    if (feedback === 'won') return;
    const g = normalize(guess);
    if (!g) return;
    setFeedback(g === normalize(songTitle) ? 'won' : 'wrong');
  };

  return (
    <div className="space-y-4">
      <span className="font-mono text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
        Guess the song
      </span>

      <div className="flex items-center gap-3 rounded-2xl border bg-muted/30 p-3">
        <Volume2 className="h-5 w-5 shrink-0 text-muted-foreground" />
        <audio controls src={audioUrl} className="w-full" />
      </div>
      {playDuration > 0 && (
        <p className="text-center text-xs text-muted-foreground">
          Participants get about {playDuration} seconds — listen carefully!
        </p>
      )}

      <div className="space-y-2">
        <label htmlFor="song-guess" className="text-sm font-medium text-foreground">
          Song title
        </label>
        <input
          id="song-guess"
          value={guess}
          onChange={(e) => {
            setGuess(e.target.value);
            if (feedback === 'wrong') setFeedback('idle');
          }}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          disabled={feedback === 'won'}
          placeholder="What song is this?"
          className={cn(
            'w-full rounded-xl border bg-card px-3.5 py-3 text-base outline-none',
            feedback === 'wrong' ? 'border-red-500' : 'border-border focus:border-primary',
          )}
        />
        {!!artist && feedback !== 'won' && (
          <p className="text-xs text-muted-foreground">Hint: by {artist}</p>
        )}
      </div>

      {feedback === 'wrong' && (
        <p className="rounded-xl bg-red-50 p-3 text-center text-sm text-red-700">
          Not quite — listen again and try another title.
        </p>
      )}
      {feedback === 'won' && (
        <p className="rounded-xl bg-emerald-50 p-3 text-center text-sm font-semibold text-emerald-700">
          That&apos;s it! &quot;{songTitle}&quot;{artist ? ` — ${artist}` : ''}.
        </p>
      )}

      {feedback !== 'won' && (
        <Button onClick={submit} disabled={!guess.trim()}>
          Submit guess
        </Button>
      )}
    </div>
  );
}
